import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Copy, Share2, Gift, ArrowRight, CheckCircle2, Users } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function Referrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/referrals/me");
        setData(data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const signupUrl =
    data?.referral_code
      ? `${window.location.origin}/driver-signup?ref=${data.referral_code}`
      : "";

  const copy = async () => {
    if (!signupUrl) return;
    try {
      await navigator.clipboard.writeText(signupUrl);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy failed");
    }
  };

  const share = async () => {
    if (!signupUrl || !navigator.share) {
      copy();
      return;
    }
    try {
      await navigator.share({
        title: "Join boatrvmoving as a driver",
        text: "Get on the platform hauling boats + RVs — use my link:",
        url: signupUrl,
      });
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-3xl mx-auto" data-testid="referrals-page">
      <div className="mb-10">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Refer</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
          Bring a driver.
          <br />
          <span className="text-[#FF3B30]">Get a month free.</span>
        </h1>
        <p className="text-sm text-zinc-500 mt-4 max-w-xl">
          Every driver you bring on who completes KYC verification unlocks
          <span className="font-bold text-white"> 30 days of Driver Pro</span> for you — stacks if you refer multiple.
        </p>
      </div>

      {loading ? (
        <div className="h-40 bg-[#0F0F10] border border-white/[0.08] animate-pulse" />
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="border border-white/[0.08] bg-[#0F0F10] p-6 relative overflow-hidden"
            data-testid="referral-card"
          >
            <div className="absolute -right-10 -top-10 w-40 h-40 bg-[#FF3B30]/20 blur-[80px]" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-3">Your code</div>
              <div className="font-display text-6xl font-black uppercase tracking-widest" data-testid="referral-code">
                {data.referral_code || "—"}
              </div>
              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Shareable link</div>
                <div className="flex items-center gap-2 bg-[#141416] border border-white/[0.08] p-3">
                  <code className="text-xs md:text-sm text-zinc-300 truncate flex-1" data-testid="referral-link">{signupUrl}</code>
                  <button
                    onClick={copy}
                    data-testid="copy-link"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold border border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] px-3 py-2 transition-colors"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                  <button
                    onClick={share}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold border border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] px-3 py-2 transition-colors"
                  >
                    <Share2 className="w-3 h-3" /> Share
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] mt-8">
            <StatTile label="Total invited" value={data.total_referred} icon={Users} accent="text-zinc-300" testid="stat-total" />
            <StatTile label="Verified" value={data.verified_referred} icon={CheckCircle2} accent="text-emerald-400" testid="stat-verified" />
            <StatTile label="Free months granted" value={data.bonus_months_granted} icon={Gift} accent="text-[#FF3B30]" testid="stat-bonus" />
          </div>

          {data.pro_active && (
            <div className="mt-6 border border-emerald-500/40 bg-emerald-500/[0.06] p-4 flex items-center gap-3" data-testid="pro-status">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <div>
                <div className="font-bold text-sm uppercase tracking-widest">Driver Pro is active</div>
                <div className="text-xs text-zinc-400">
                  {data.pro_source === "referral" ? "Earned via referrals" : "Paid subscription"}
                  {data.pro_expires_at ? ` · Expires ${new Date(data.pro_expires_at).toLocaleDateString()}` : ""}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-[#0F0F10] p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{label}</div>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <div className="font-display text-5xl font-black uppercase mt-3 leading-none">{value}</div>
    </div>
  );
}
